import React, { useCallback, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Mark, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import FontFamily from "@tiptap/extension-font-family";
import { Box, IconButton, Divider, Select, MenuItem } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import FormatClearIcon from "@mui/icons-material/FormatClear";

const fontFamilies = [
  "Helvetica",
  "Tahoma",
  "Open Sans",
  "Roboto",
  "Georgia",
  "Times New Roman",
  "Garamond",
  "Trebuchet MS",
];

// Author-selectable font-size scale. Each option is written to the saved HTML
// as an inline mark with the given class (e.g. <span class="fs-md">). The `px`
// value is the desktop size shown to the author; the webapp applies the actual
// responsive clamp() via `.sales-richtext__body .fs-*` CSS. See SALES_PAGES_CMS.md.
const FONT_SIZE_OPTIONS = [
  { className: "fs-xxxs", label: "Font-XXXS", px: 8 },
  { className: "fs-xxs", label: "Font-XXS", px: 10 },
  { className: "fs-xs", label: "Font-XS", px: 12 },
  { className: "fs-sm", label: "Font-SM", px: 14 },
  { className: "fs-md", label: "Font-MD", px: 18 },
  { className: "fs-lg", label: "Font-LG", px: 22 },
  { className: "fs-xl", label: "Font-XL", px: 26 },
  { className: "fs-xxl", label: "Font-XXL", px: 30 },
  { className: "fs-xxxl", label: "Font-XXXL", px: 36 },
  { className: "fs-xxxxl", label: "Font-XXXXL", px: 48 },
];

const FONT_SIZE_CLASSES = FONT_SIZE_OPTIONS.map((o) => o.className);

// Custom inline mark that stores the chosen font-size as a class on a <span>.
// Selecting a size wraps the selection; the class round-trips through the HTML.
const FontSizeClass = Mark.create({
  name: "fontSizeClass",
  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (element) => {
          const cls = (element.getAttribute("class") || "")
            .split(/\s+/)
            .find((c) => FONT_SIZE_CLASSES.includes(c));
          return cls || null;
        },
        renderHTML: (attributes) =>
          attributes.class ? { class: attributes.class } : {},
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: "span",
        getAttrs: (element) => {
          const has = (element.getAttribute("class") || "")
            .split(/\s+/)
            .some((c) => FONT_SIZE_CLASSES.includes(c));
          return has ? {} : false;
        },
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
  addCommands() {
    return {
      setFontSizeClass:
        (className) =>
        ({ chain }) =>
          chain().setMark(this.name, { class: className }).run(),
      unsetFontSizeClass:
        () =>
        ({ chain }) =>
          chain().unsetMark(this.name).run(),
    };
  },
});

const MenuBar = ({ editor, enableFontSize = false }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }, [editor]);

  if (!editor) return null;

  const currentFontFamily = editor.getAttributes("textStyle").fontFamily || "";

  // The styles dropdown is one mutually-exclusive control covering block styles
  // (Normal / Heading 1-4) and, when enabled, the inline font-size classes.
  const activeFontSize = enableFontSize
    ? editor.getAttributes("fontSizeClass").class
    : null;
  const currentStyle = activeFontSize
    ? activeFontSize
    : editor.isActive("heading", { level: 1 })
    ? "1"
    : editor.isActive("heading", { level: 2 })
    ? "2"
    : editor.isActive("heading", { level: 3 })
    ? "3"
    : editor.isActive("heading", { level: 4 })
    ? "4"
    : "0";

  const handleStyleChange = (val) => {
    const chain = editor.chain().focus();
    // Selecting a font size applies the inline mark to the selection, leaving
    // the block type as-is; any other choice clears an active font-size mark.
    if (FONT_SIZE_CLASSES.includes(val)) {
      chain.setFontSizeClass(val).run();
      return;
    }
    if (enableFontSize && activeFontSize) {
      chain.unsetFontSizeClass();
    }
    if (val === "0") {
      chain.setParagraph().run();
    } else {
      chain.toggleHeading({ level: parseInt(val, 10) }).run();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        p: 1,
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "#fafafa",
        borderTopLeftRadius: "4px",
        borderTopRightRadius: "4px",
      }}
    >
      <Select
        size="small"
        value={currentStyle}
        onChange={(e) => handleStyleChange(e.target.value)}
        sx={{
          minWidth: 100,
          height: 32,
          fontSize: "13px",
          fontFamily: "Avenir Book, sans-serif",
        }}
      >
        <MenuItem value="0" sx={{ fontSize: "13px" }}>
          Normal
        </MenuItem>
        <MenuItem value="1" sx={{ fontSize: "13px" }}>
          Heading 1
        </MenuItem>
        <MenuItem value="2" sx={{ fontSize: "13px" }}>
          Heading 2
        </MenuItem>
        <MenuItem value="3" sx={{ fontSize: "13px" }}>
          Heading 3
        </MenuItem>
        <MenuItem value="4" sx={{ fontSize: "13px" }}>
          Heading 4
        </MenuItem>
        {enableFontSize &&
          FONT_SIZE_OPTIONS.map((opt) => (
            <MenuItem
              key={opt.className}
              value={opt.className}
              sx={{ fontSize: "13px" }}
            >
              {`${opt.label} — ${opt.px}px`}
            </MenuItem>
          ))}
      </Select>

      <Select
        size="small"
        value={currentFontFamily}
        displayEmpty
        onChange={(e) => {
          const val = e.target.value;
          if (val) {
            editor.chain().focus().setFontFamily(val).run();
          } else {
            editor.chain().focus().unsetFontFamily().run();
          }
        }}
        sx={{
          minWidth: 120,
          height: 32,
          fontSize: "13px",
          fontFamily: "Avenir Book, sans-serif",
        }}
      >
        <MenuItem value="" sx={{ fontSize: "13px" }}>
          Default Font
        </MenuItem>
        {fontFamilies.map((font) => (
          <MenuItem
            key={font}
            value={font}
            sx={{ fontSize: "13px", fontFamily: font }}
          >
            {font}
          </MenuItem>
        ))}
      </Select>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBold().run()}
        color={editor.isActive("bold") ? "primary" : "default"}
        title="Bold"
      >
        <FormatBoldIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        color={editor.isActive("italic") ? "primary" : "default"}
        title="Italic"
      >
        <FormatItalicIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        color={editor.isActive("underline") ? "primary" : "default"}
        title="Underline"
      >
        <FormatUnderlinedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        color={editor.isActive("strike") ? "primary" : "default"}
        title="Strikethrough"
      >
        <StrikethroughSIcon fontSize="small" />
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        color={editor.isActive("orderedList") ? "primary" : "default"}
        title="Numbered List"
      >
        <FormatListNumberedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        color={editor.isActive("bulletList") ? "primary" : "default"}
        title="Bullet List"
      >
        <FormatListBulletedIcon fontSize="small" />
      </IconButton>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <input
        type="color"
        title="Text Color"
        onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        value={editor.getAttributes("textStyle").color || "#000000"}
        style={{
          width: 28,
          height: 28,
          border: "1px solid #ccc",
          borderRadius: 4,
          padding: 1,
          cursor: "pointer",
        }}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <IconButton
        size="small"
        onClick={setLink}
        color={editor.isActive("link") ? "primary" : "default"}
        title="Link"
      >
        <LinkIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        title="Clear Formatting"
      >
        <FormatClearIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

/**
 * RichTextEditor - A TipTap-based rich text editor component.
 *
 * Props:
 * - value: HTML string to display
 * - onChange: callback receiving updated HTML string
 * - minHeight: minimum editor height (default "100px")
 * - disabled: if true, editor is read-only
 * - enableFontSize: if true, expose the author-selectable font-size classes in
 *   the styles dropdown (used by the sales rich-text sections)
 */
const RichTextEditor = ({
  value,
  onChange,
  minHeight = "100px",
  disabled = false,
  enableFontSize = false,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit includes: bold, italic, strike, heading, bulletList,
        // orderedList, listItem, blockquote, codeBlock, hardBreak, etc.
        // HardBreak handles Shift+Enter → <br> natively
        heading: { levels: [1, 2, 3, 4] },
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      ...(enableFontSize ? [FontSizeClass] : []),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  // Update content when value prop changes externally (e.g., loading from Firestore)
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentHtml = editor.getHTML();
      // Only update if the content actually differs to avoid cursor jumps
      if (currentHtml !== value) {
        editor.commands.setContent(value || "", false);
      }
    }
  }, [editor, value]);

  // Update editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) return null;

  // Preview the font-size classes inside the editor using their desktop px so
  // authors see the size they picked (the webapp applies the responsive clamp).
  const fontSizePreviewStyles = enableFontSize
    ? FONT_SIZE_OPTIONS.reduce((acc, opt) => {
        acc[`& .${opt.className}`] = { fontSize: `${opt.px}px` };
        return acc;
      }, {})
    : {};

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        "&:focus-within": {
          borderColor: "#0AB2EE",
        },
        "& .ProseMirror": {
          minHeight,
          padding: "12px",
          outline: "none",
          fontFamily: "Avenir Book, sans-serif",
          fontSize: "16px",
          lineHeight: "1.6",
          "& p": {
            margin: "0 0 16px 0",
          },
          "& p:last-child": {
            marginBottom: 0,
          },
          "& ol, & ul": {
            paddingLeft: "24px",
          },
          "& a": {
            color: "#0AB2EE",
            textDecoration: "underline",
          },
          ...fontSizePreviewStyles,
        },
      }}
    >
      {!disabled && <MenuBar editor={editor} enableFontSize={enableFontSize} />}
      <EditorContent editor={editor} />
    </Box>
  );
};

export default RichTextEditor;
